import './ProductPurchaseModal.css';

import { useState } from 'react';

import Checkbox from '../../../components/checkbox/Checkbox';
import ExpandableSection from '../../../components/expandable-section/ExbandableSection';
import Paragraph from '../../../components/paragraph/Paragraph';
import type { Action, Content, ProductPurchaseModalDetails, PurchaseAction } from '../../../global-types/Content';
import useTracking from '../../../hooks/useTracking';
import { renderContent } from '../../../utils/renderContent';

type ProductPurchaseModalProps = {
  content: Content;
  viewId: number | undefined;
  dispatchActionEvent: (action: Action, nextContent?: Content | null) => void;
  modalOpen: boolean;
  disableButton: boolean;
  setDisableButton: (value: boolean) => void;
};

function ProductPurchaseModal({
  content,
  viewId,
  dispatchActionEvent,
  modalOpen,
  disableButton,
  setDisableButton,
}: ProductPurchaseModalProps) {
  const view = viewId ? content.contentViews?.find((cv) => cv.contentViewId === viewId) : undefined;
  const trackVisibilityElement = useTracking(content, modalOpen ? viewId : undefined);
  const [isChecked, setIsChecked] = useState(false);
  const [showCheckboxError, setShowCheckboxError] = useState(false);

  if (!view) return null;

  const {
    header,
    priceDetails,
    priceDetailsHelperText,
    productExpandable,
    termsAndConditionsCheckbox,
    termsAndConditionsCheckboxError,
    purchaseButtonHelperText,
  } = (view.viewElements.texts as ProductPurchaseModalDetails) || {};

  const { linkStyledActions, buttonStyledActions } = renderContent({
    view,
    trackVisibilityElement,
    actionsConfig: {
      dispatchActionEvent,
      validateAction: (action: Action) => {
        const isPurchaseButton = !!(action as PurchaseAction).purchaseUnhandledErrorMessage;
        if (isPurchaseButton && !!termsAndConditionsCheckbox && !isChecked) {
          setShowCheckboxError(true);
          return false;
        }
        return true;
      },
      skipFirstAction: true,
      disableButton,
      setDisableButton,
    },
  });

  const { texts: headerTexts } = renderContent({ texts: header, trackVisibilityElement });

  return (
    <div
      className="if modal-v19-content gap-16 small-padding flex-column product-purchase-modal"
      id="modal-v19-content"
    >
      <div className="if px-8 flex-center flex-column gap-24">
        {headerTexts}
        {priceDetails && (
          <div className="if price-details size-w-100p">
            <div className="if card elevated mb-8">
              <div className="if card-content flex-column gap-4">
                {priceDetails.map((row, i) => (
                  <div key={i}>
                    {row.rowType === 'total' && priceDetails[i - 1]?.rowType !== 'total' && (
                      <hr className="if border-top-solid color-beige-400 my-8" />
                    )}
                    <div className="if flex justify-content-space-between">
                      <Paragraph text={row.label.value} size={row.label.size} color={row.label.color} />
                      <Paragraph
                        text={row.amount.value}
                        size={row.amount.size}
                        color={row.amount.color}
                        bold={row.rowType === 'total' && priceDetails[i - 1]?.rowType !== 'total'}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {priceDetailsHelperText?.map((text, i) => (
              <Paragraph key={i + text.value} text={text.value} size={text.size} />
            ))}
          </div>
        )}
        <ExpandableSection title={productExpandable?.value ?? ''}>{linkStyledActions}</ExpandableSection>
        {termsAndConditionsCheckbox && termsAndConditionsCheckboxError && (
          <Checkbox
            isChecked={isChecked}
            setIsChecked={setIsChecked}
            showCheckboxError={showCheckboxError}
            setShowCheckboxError={setShowCheckboxError}
            checkboxLabel={termsAndConditionsCheckbox.value}
            checkboxError={termsAndConditionsCheckboxError.value}
          />
        )}

        {purchaseButtonHelperText?.map((text, i) => (
          <div key={i + text.value} className="if size-w-100p">
            <Paragraph text={text.value} size={text.size} />
          </div>
        ))}

        <div className="if gap-16 flex-center flex-column size-w-100p">{buttonStyledActions}</div>
      </div>
    </div>
  );
}

export default ProductPurchaseModal;
